// utils/highlightText.ts

/**
 * Highlights matched text in a string by wrapping it in mark elements
 * This function works with plain text (no HTML tags expected)
 *
 * @param text The text to search within (plain text)
 * @param searchTerm The search term to highlight
 * @returns React elements with matched text wrapped in mark tags
 */
export const highlightText = (text: string, searchTerm: string) => {
  if (!searchTerm || !text) return text;

  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) => {
    if (part && regex.test(part)) {
      // Reset regex lastIndex to avoid issues with global flag
      regex.lastIndex = 0;
      return (
        <mark key={index} className="bg-yellow-400/50">
          {part}
        </mark>
      );
    }
    // Reset regex lastIndex for next iteration
    regex.lastIndex = 0;
    return part;
  });
};

/**
 * Highlights matched text and returns HTML string for dangerouslySetInnerHTML
 * This function works with plain text (no HTML tags expected)
 *
 * @param text The text to search within (plain text)
 * @param searchTerm The search term to highlight
 * @returns HTML string with matched text wrapped in highlighted spans
 */
export const highlightTextAsHTML = (text: string, searchTerm: string): string => {
  if (!searchTerm || !text) return text;

  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');

  return text.replace(regex, '<mark class="bg-yellow-400/50">$1</mark>');
};
